import {useState, useCallback, type FormEvent} from 'react';
import {Redirect, Switch, Route} from 'react-router-dom';

import {appHistory} from '../../history';
import {DebugPage} from '../../ui/debug/debug-page';
import {DebugSidebar} from '../../ui/debug/debug-sidebar';
import {EmailDebugPage} from '../../ui/debug/email-debug-page/email-debug-page';
import {PhtmlDebugPage} from '../../ui/debug/phtml-debug-page';
import {EmailDigestPage} from '../../ui/email-digest/email-digest-page';
import {PageWrapper} from '../../ui/layout/page-wrapper';

/**
 * Landing page for entity-ID-based debug tools. Shows a text input
 * so the user can enter an entity API ID (e.g. "123-leads") before
 * navigating to the actual tool page.
 */
function EntityIdLanding(props: {
    basePath: string;
    title: string;
    subtitle: string;
}): React.ReactElement {
    const {basePath, title, subtitle} = props;
    const [entityApiId, setEntityApiId] = useState('');

    const handleSubmit = useCallback(
        (e: FormEvent) => {
            e.preventDefault();
            const trimmed = entityApiId.trim();
            if (trimmed) {
                appHistory.push(`${basePath}/${trimmed}`);
            }
        },
        [entityApiId, basePath]
    );

    return (
        <DebugPage title={title} subtitle={subtitle}>
            <form onSubmit={handleSubmit} className='flex gap-8' style={{alignItems: 'flex-end'}}>
                <div style={{flex: 1}}>
                    <label
                        style={{
                            display: 'block',
                            fontSize: 11,
                            fontWeight: 600,
                            color: '#495057',
                            textTransform: 'uppercase',
                            marginBottom: 4,
                        }}
                    >
                        Entity API ID
                    </label>
                    <input
                        type='text'
                        value={entityApiId}
                        onChange={(e) => setEntityApiId(e.target.value)}
                        placeholder='e.g. 123-leads or 456-contacts'
                        style={{
                            width: '100%',
                            padding: '8px 10px',
                            border: '1px solid #ced4da',
                            borderRadius: 4,
                            fontSize: 13,
                        }}
                    />
                </div>
                <button
                    type='submit'
                    disabled={!entityApiId.trim()}
                    style={{
                        padding: '8px 16px',
                        background: entityApiId.trim() ? '#007bff' : '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        fontSize: 12,
                        cursor: entityApiId.trim() ? 'pointer' : 'not-allowed',
                    }}
                >
                    Open
                </button>
            </form>
        </DebugPage>
    );
}

function Ai2SandboxLanding(): React.ReactElement {
    return (
        <EntityIdLanding
            basePath='/debug/ai2'
            title='AI v2 Sandbox'
            subtitle='Enter an entity API ID to test the AI v2 summarization pipeline.'
        />
    );
}

function Ai2SandboxPage(props: {match: {params: {entityApiId: string}}}): React.ReactElement {
    return (
        <PhtmlDebugPage
            originalUrl={`/ai2/view/${props.match.params.entityApiId}`}
            title='AI v2 Sandbox'
            subtitle='Test AI v2 summarization pipeline for an entity timeline.'
        />
    );
}

function AiSandboxLanding(): React.ReactElement {
    return (
        <EntityIdLanding
            basePath='/debug/ai'
            title='AI v1 Sandbox'
            subtitle='Enter an entity API ID to test the AI v1 summarization pipeline.'
        />
    );
}

function AiSandboxPage(props: {match: {params: {entityApiId: string}}}): React.ReactElement {
    return (
        <PhtmlDebugPage
            originalUrl={`/ai/view/${props.match.params.entityApiId}`}
            title='AI v1 Sandbox'
            subtitle='Test AI v1 summarization pipeline for an entity timeline.'
        />
    );
}

function LeadResearchPage(): React.ReactElement {
    return (
        <PhtmlDebugPage
            originalUrl='/lead-research/index'
            title='Lead Research'
            subtitle='AI-powered lead research sandbox.'
        />
    );
}

function EnrichmentPage(): React.ReactElement {
    const searchParams = window.location.search;

    return (
        <PhtmlDebugPage
            originalUrl={`/enrichment/index${searchParams}`}
            title='Enrichment'
            subtitle='FullContact V3 enrichment debug tool.'
        />
    );
}

function MarketingAiPage(): React.ReactElement {
    return (
        <PhtmlDebugPage
            originalUrl='/marketing/ai-debug'
            title='Marketing AI Debug'
            subtitle='AI campaign generation debug pipeline.'
        />
    );
}

function AiImageGenPage(): React.ReactElement {
    return (
        <PhtmlDebugPage
            originalUrl='/ai-image-gen/index'
            title='AI Image Gen'
            subtitle='AI image generation sandbox (Gemini/Imagen).'
        />
    );
}

function ThreadSummaryLanding(): React.ReactElement {
    return (
        <EntityIdLanding
            basePath='/debug/thread-summary'
            title='Thread AI Summary'
            subtitle='Enter a chat thread API ID to test the AI thread summarization pipeline.'
        />
    );
}

function ThreadSummaryPage(props: {match: {params: {entityApiId: string}}}): React.ReactElement {
    return (
        <PhtmlDebugPage
            originalUrl={`/ai/chatbot-thread-summary?id=${props.match.params.entityApiId}`}
            title='Thread AI Summary'
            subtitle='AI summarization sandbox for chatbot threads.'
        />
    );
}

function LeadSummaryLanding(): React.ReactElement {
    return (
        <EntityIdLanding
            basePath='/debug/lead-summary'
            title='Lead Summary'
            subtitle='Enter a lead API ID to test the AI lead summarization pipeline.'
        />
    );
}

function LeadSummaryPage(props: {match: {params: {entityApiId: string}}}): React.ReactElement {
    return (
        <PhtmlDebugPage
            originalUrl={`/ai/lead-summary?id=${props.match.params.entityApiId}`}
            title='Lead Summary'
            subtitle='AI lead summarization sandbox.'
        />
    );
}

function EmailRepliesLanding(): React.ReactElement {
    return (
        <EntityIdLanding
            basePath='/debug/email-replies'
            title='Email Replies'
            subtitle='Enter an email API ID to test the AI suggested reply generation.'
        />
    );
}

function EmailRepliesPage(props: {match: {params: {entityApiId: string}}}): React.ReactElement {
    return (
        <PhtmlDebugPage
            originalUrl={`/ai/email-replies?id=${props.match.params.entityApiId}`}
            title='Email Replies'
            subtitle='AI suggested email reply sandbox.'
        />
    );
}

function TaskGenerationLanding(): React.ReactElement {
    return (
        <EntityIdLanding
            basePath='/debug/task-generation'
            title='Task Generation'
            subtitle='Enter an activity API ID to test AI task generation from meeting transcripts.'
        />
    );
}

function TaskGenerationPage(props: {match: {params: {entityApiId: string}}}): React.ReactElement {
    return (
        <PhtmlDebugPage
            originalUrl={`/ai/task-generation?id=${props.match.params.entityApiId}`}
            title='Task Generation'
            subtitle='AI task generation sandbox for meeting transcripts.'
        />
    );
}

function ChatbotReportPage(): React.ReactElement {
    return (
        <PhtmlDebugPage
            originalUrl='/ai/chatbot-report'
            title='Chatbot Report'
            subtitle='AI chatbot report generation sandbox.'
        />
    );
}

export function DebugRoot(): React.ReactElement {
    if (!NutClientConfig.isInternalLogin) {
        return <Redirect to='/' />;
    }

    return (
        <PageWrapper sidebar={<DebugSidebar />}>
            <Switch>
                <Redirect exact={true} from='/debug' to='/debug/email' />
                <Route exact={true} path='/debug/email' component={EmailDebugPage} />
                <Route exact={true} path='/debug/email-digest' component={EmailDigestPage} />
                <Route exact={true} path='/debug/ai2' component={Ai2SandboxLanding} />
                <Route path='/debug/ai2/:entityApiId' component={Ai2SandboxPage} />
                <Route exact={true} path='/debug/ai' component={AiSandboxLanding} />
                <Route path='/debug/ai/:entityApiId' component={AiSandboxPage} />
                <Route exact={true} path='/debug/lead-research' component={LeadResearchPage} />
                <Route exact={true} path='/debug/enrichment' component={EnrichmentPage} />
                <Route exact={true} path='/debug/marketing-ai' component={MarketingAiPage} />
                <Route exact={true} path='/debug/ai-image-gen' component={AiImageGenPage} />
                <Route exact={true} path='/debug/thread-summary' component={ThreadSummaryLanding} />
                <Route path='/debug/thread-summary/:entityApiId' component={ThreadSummaryPage} />
                <Route exact={true} path='/debug/lead-summary' component={LeadSummaryLanding} />
                <Route path='/debug/lead-summary/:entityApiId' component={LeadSummaryPage} />
                <Route exact={true} path='/debug/email-replies' component={EmailRepliesLanding} />
                <Route path='/debug/email-replies/:entityApiId' component={EmailRepliesPage} />
                <Route
                    exact={true}
                    path='/debug/task-generation'
                    component={TaskGenerationLanding}
                />
                <Route path='/debug/task-generation/:entityApiId' component={TaskGenerationPage} />
                <Route exact={true} path='/debug/chatbot-report' component={ChatbotReportPage} />
            </Switch>
        </PageWrapper>
    );
}
